import '../vendor/kanmon-hero/layer1-grid.js';
import '../vendor/kanmon-hero/kanmon-lightsweep.js';
import gsap from 'gsap';

const OPTIONS = {
    greenHex: '#219419',
    blueHex: '#265769',
    sweepSeconds: 1.4,
    restSeconds: 3,
    brightness: 0.9,
    lightSize: 120,
    lightTrailMs: 330,
    waveLag: 0.16,
    curveAmount: 0.2,
    cursor: true,
    cursorStrength: 1,
};

const RESIZE_DELAY = 150;
const MIN_HERO_CANVAS_HEIGHT_RATIO = 0.33;
const CLIENT_LOGO_SLIDING_DURATION_IN_SECONDS = 30;
const CLIENT_LOGO_SLIDER_RETRY_DELAY = 100;
const CLIENT_LOGO_SLIDER_MAX_RETRIES = 10;

export const name = 'home';

function getDisplayWidth(canvas) {
    const grid = window.KANMON_LAYER1_GRID;
    const parent = canvas.parentElement;
    const parentWidth = parent?.clientWidth || canvas.clientWidth;
    const parentHeight = parent?.clientHeight || canvas.clientHeight;

    if (grid && parentWidth && parentHeight) {
        return Math.round(
            Math.max(
                parentWidth,
                parentHeight
                    * MIN_HERO_CANVAS_HEIGHT_RATIO
                    * (grid.width / grid.height)
            )
        );
    }

    return Math.round(
        parentWidth
        || canvas.clientWidth
        || grid?.width
        || 1360
    );
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function whenImagesReady(images, callback) {
    if (images.length === 0) {
        callback();
        return;
    }

    let ready = 0;
    const settled = new WeakSet();

    function handleImageReady(image) {
        if (settled.has(image)) {
            return;
        }

        settled.add(image);
        image.removeEventListener('load', handleReady);
        image.removeEventListener('error', handleReady);
        ready += 1;

        if (ready === images.length) {
            callback();
        }
    }

    function handleReady(event) {
        handleImageReady(event.currentTarget);
    }

    images.forEach((image) => {
        image.addEventListener('load', handleReady);
        image.addEventListener('error', handleReady);

        if (image.complete) {
            handleImageReady(image);
        }
    });
}

function getClientLogoSliderPosition(slider) {
    const firstGroup = slider.querySelector('[data-slides]');
    const groupWidth = firstGroup?.offsetWidth || 0;

    if (!firstGroup || groupWidth <= 0) {
        return 0;
    }

    const x = Number(gsap.getProperty(firstGroup, 'x')) || 0;
    const distance = ((-x % groupWidth) + groupWidth) % groupWidth;

    return distance / groupWidth;
}

function prepareClientLogoSlider(
    slider,
    attempt = 0,
    onPrepared = null,
    initialPosition = 0
) {
    const groups = Array.from(slider.querySelectorAll('[data-slides]'));
    const firstGroup = groups[0];

    if (!firstGroup || groups.length < 2 || prefersReducedMotion()) {
        return null;
    }

    const groupWidth = firstGroup.offsetWidth;

    if (groupWidth <= 0) {
        if (attempt < CLIENT_LOGO_SLIDER_MAX_RETRIES) {
            window.setTimeout(
                () => {
                    const context = prepareClientLogoSlider(
                        slider,
                        attempt + 1,
                        onPrepared,
                        initialPosition
                    );

                    if (context && onPrepared) {
                        onPrepared(context);
                    }
                },
                CLIENT_LOGO_SLIDER_RETRY_DELAY
            );
        }

        return null;
    }

    const context = gsap.context(() => {
        const timeline = gsap.timeline();
        const initialX = -groupWidth * initialPosition;

        gsap.set(groups, { x: initialX });

        timeline.to(
            groups,
            {
                duration: CLIENT_LOGO_SLIDING_DURATION_IN_SECONDS,
                ease: 'none',
                x: `-=${groupWidth}`,
                modifiers: {
                    x: gsap.utils.unitize(
                        gsap.utils.wrap(
                            -groupWidth,
                            0
                        )
                    ),
                },
                repeat: -1,
            }
        );
    }, slider);

    return context;
}

function prepareClientLogoSliders() {
    const sliders = Array.from(document.querySelectorAll('[data-client-logo-slider]'));
    let contexts = [];

    const addContext = (context) => {
        if (context) {
            contexts.push(context);
        }
    };

    sliders.forEach((slider) => {
        const firstGroup = slider.querySelector('[data-slides]');

        whenImagesReady(
            Array.from(firstGroup?.querySelectorAll('img') || []),
            () => addContext(prepareClientLogoSlider(slider, 0, addContext))
        );
    });

    let resizeTimer;

    window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);

        resizeTimer = window.setTimeout(() => {
            const sliderPositions = new Map(
                sliders.map((slider) => [
                    slider,
                    getClientLogoSliderPosition(slider),
                ])
            );

            contexts.forEach((context) => {
                context.revert();
            });

            contexts = sliders
                .map((slider) => prepareClientLogoSlider(
                    slider,
                    0,
                    addContext,
                    sliderPositions.get(slider) || 0
                ))
                .filter(Boolean);
        }, RESIZE_DELAY);
    });
}

export function initialize() {
    const canvas = document.querySelector('[data-kanmon-hero-bg]');

    prepareClientLogoSliders();

    if (!canvas || !window.KanmonLightSweep) {
        return;
    }

    const sweep = window.KanmonLightSweep.create(canvas, {
        ...OPTIONS,
        displayWidth: getDisplayWidth(canvas),
    });

    if (!prefersReducedMotion()) {
        sweep.start();
    }

    let resizeTimer;

    window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);

        resizeTimer = window.setTimeout(() => {
            sweep.resize(getDisplayWidth(canvas));
        }, RESIZE_DELAY);
    });
}
